import { useState } from 'react'
import { DialogDescription, DialogHeader, DialogTitle, DialogClose, DialogContent } from '@/components'
import AlertTriangle from '@/assets/icons/alert-triangle'
import { Divider, Button } from '@/components'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { ToastMessage } from '@/components/toast-message'
import { toast } from 'sonner'
import { format } from 'date-fns'
import Amount from '@/pages/transactions-page/Amount'
import { transformAmount } from '@/utils/string-format-utils'
import { useAuth } from '@/contexts/auth.context'
import LoadingScreen from '@/components/loading-screen'
import { convertUTCToET } from '@/utils/date-format-util'
import PropTypes from 'prop-types'

// Define constants for each option
const GENERAL_QUESTION = 'General Question'
const REWARDS = 'Rewards'
const I_NEED_TO_DISPUTE = 'I Need to Dispute'

function ReportProblemDialog({ transaction }) {
  const [selectedReason, setSelectedReason] = useState(GENERAL_QUESTION)

  const { isLoading: isCustomerDataLoading, user } = useAuth()

  async function onReportProblem() {
    const zendeskFastenUrlRewards = import.meta.env.VITE_ZENDESK_FASTEN_REWARDS_URL
    const zendeskFastenUrlGeneral = import.meta.env.VITE_ZENDESK_FASTEN_GENERAL_URL
    const zendeskHighnoteUrl = import.meta.env.VITE_ZENDESK_HIGHNOTE_URL

    //params for URL construction
    const email = user?.customer?.email
    const financialAccountId = user?.customer?.financialAccountId
    const transactionDate = format(new Date(convertUTCToET(transaction?.transactionDate)), 'yyyy-MM-dd')
    const transactionAmount = transformAmount(transaction?.amount?.value, transaction?.sign).replace(/\s?\$/g, '')
    const transactionAmountRewards = transaction?.amount?.value / 100
    const merchantName = (transaction?.merchantName || transaction?.merchantDetails?.name)?.replace(/\s+/g, '-') || ''
    const transactionId = transaction?.id
    const firstName = user?.customer?.firstName || ''
    const lastName = user?.customer?.lastName || ''

    // Construct the Fasten Zendesk URL with query parameters
    const fastenUrlWithParamsGeneral = `${zendeskFastenUrlGeneral}&tf_33278072912403=${encodeURIComponent(merchantName)}&tf_33278111662995=${transactionAmountRewards}&tf_33278065317011=${encodeURIComponent(transactionDate)}&tf_32234787345165=${transactionId}&tf_33278033734931=${financialAccountId}&tf_anonymous_requester_email=${encodeURIComponent(email)}&tf_35196931160595=${encodeURIComponent(firstName)}&tf_35196910565395=${encodeURIComponent(lastName)}`
    const fastenUrlWithParamsRewards = `${zendeskFastenUrlRewards}&tf_33278072912403=${encodeURIComponent(merchantName)}&tf_33278111662995=${transactionAmountRewards}&tf_33278065317011=${encodeURIComponent(transactionDate)}&tf_32234787345165=${transactionId}&tf_33278033734931=${financialAccountId}&tf_anonymous_requester_email=${encodeURIComponent(email)}&tf_35196931160595=${encodeURIComponent(firstName)}&tf_35196910565395=${encodeURIComponent(lastName)}`

    // Construct the Zendesk URL with query parameters
    const highnoteUrlWithParams = `${zendeskHighnoteUrl}&tf_subject=Your+Dispute+Claim+Has+Been+Submitted&tf_18448459539597=${encodeURIComponent(merchantName)}&tf_18448952317965=${transactionAmount}&tf_18448501866637=${encodeURIComponent(transactionDate)}&tf_32234787345165=${transactionId}&tf_18448341785485=${financialAccountId}&tf_anonymous_requester_email=${encodeURIComponent(email)}&tf_18448358815373=${encodeURIComponent(firstName)}&tf_18448389721997=${encodeURIComponent(lastName)}`

    // Determine final URL based on selected dispute reason
    const getTargetUrl = () => {
      switch (selectedReason) {
        case GENERAL_QUESTION:
          return fastenUrlWithParamsGeneral
        case REWARDS:
          return fastenUrlWithParamsRewards
        case I_NEED_TO_DISPUTE:
          return highnoteUrlWithParams
        default:
          return fastenUrlWithParamsGeneral // fallback to general
      }
    }

    const targetUrl = getTargetUrl()

    // Redirect to Zendesk URL
    window.open(targetUrl, '_blank')

    setSelectedReason(GENERAL_QUESTION)
    toast.custom(() => (
      <ToastMessage
        success
        text="Problem reported successfully"
        description="Your problem has been reported successfully. Our team will review the details and follow up with you shortly"
      />
    ))
  }

  function handleDialogOpenClose() {
    setSelectedReason(GENERAL_QUESTION)
  }

  const footerContent = (
    <div className="flex flex-row gap-3 p-5">
      <DialogClose asChild>
        <Button
          id="report-problem-cancel-button"
          aria-label="Cancel"
          variant="outlineSecondary"
          className="w-full hover:bg-transparent"
        >
          Cancel
        </Button>
      </DialogClose>
      <Button
        id="report-problem-button"
        aria-label="Report Problem"
        type="submit"
        className="w-full bg-[--error-500] hover:bg-[--error-500] focus:bg-[--error-500]"
        onClick={onReportProblem}
      >
        Report Problem
      </Button>
    </div>
  )

  return (
    <DialogContent
      className="md:w-[480px] w-[94%] md:rounded-xl rounded-xl max-h-[90vh] flex flex-col"
      onClose={handleDialogOpenClose}
    >
      <DialogHeader className="flex-shrink-0">
        <div className="flex flex-row items-center gap-4">
          <div className="flex self-start items-center justify-center bg-gradient-to-r from-[--brand-850] to-[--brand-900] min-w-12 min-h-12 rounded-[10px]">
            <AlertTriangle stroke="white" />
          </div>
          <div className="flex flex-col gap-2 pt-0">
            <DialogTitle className="leading-6 text-neutral-900">Report a Problem</DialogTitle>
            <DialogDescription className="mt-[-3px] text-neutral-700 mb-[-8px]">
              {"What doesn't look right with this transaction?"}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>
      <Divider className="flex-shrink-0" />
      <div className="flex flex-col gap-5 p-6 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-[14px] pb-3">
          <p className="text-neutral-700 text-base font-normal">
            Choose the scenario that best describes what happened.
          </p>
          <p className="text-neutral-700 text-sm font-medium">Transaction</p>
          <div className="flex flex-row items-center text-sm md:p-3 p-4 rounded-[12px] md:h-[80px] min-h-[60px] border-[1px] border-solid border-brand-900">
            <div className="flex items-center border-gray-200 flex-1">
              <div className="ml-1 flex flex-col justify-between min-h-[40px]">
                <p className="text-sm font-medium text-neutral-900">
                  {transaction?.merchantName || transaction?.merchantDetails?.name || ''}
                </p>
                <p className="text-sm font-regular text-neutral-700">
                  {transaction?.merchantDetails?.description || ''}
                </p>
              </div>
            </div>
            {/* Add margin to create a gap */}
            <div className="flex items-center pt-2 pr-2 pb-2 pl-2 justify-end border-gray-200 flex-shrink-0">
              <div>
                <p className="ml-1 text-sm font-bold flex justify-end">
                  <Amount amount={transaction?.amount?.value} sign={'POSITIVE'} customColor="text-neutral-900" />
                </p>
                <p className="ml-8 text-sm font-regular text-neutral-700 flex justify-end">
                  {transaction?.transactionDate
                    ? format(new Date(convertUTCToET(transaction.transactionDate)), 'MM/dd/yy')
                    : 'Date'}
                </p>
              </div>
            </div>
          </div>
        </div>
        {isCustomerDataLoading ? (
          <LoadingScreen />
        ) : (
          <>
            {/* Define Radio Buttons */}
            <RadioGroup
              defaultValue={GENERAL_QUESTION}
              value={selectedReason}
              onValueChange={value => {
                setSelectedReason(value)
              }}
              className="md:mx-4 mx-2 mb-6 md:mb-0"
            >
              {/*Define contents*/}
              {/* Option one */}
              <div className="md:mx-2 mx-3 w-full">
                <div
                  className="flex items-center space-x-2  md:h-[20px] w-full h-[20px] cursor-pointer"
                  onClick={() => setSelectedReason(GENERAL_QUESTION)}
                >
                  <RadioGroupItem value={GENERAL_QUESTION} id={GENERAL_QUESTION} selection={selectedReason} />
                  <Label className={`text-[--base-black] font-medium cursor-pointer`} htmlFor={GENERAL_QUESTION}>
                    General Question
                  </Label>
                </div>
                <div
                  className="ml-6 text-sm flex-1 mb-3 text-neutral-700 font-normal cursor-pointer"
                  onClick={() => setSelectedReason(GENERAL_QUESTION)}
                >
                  I have a general question I’d like to ask customer service.
                </div>
              </div>

              {/* Option two */}
              <div className="md:ml-2 ml-3">
                <div
                  className="flex items-center space-x-2  md:w-[320px] md:h-[20px] w-[239px] h-[20px] cursor-pointer"
                  onClick={() => setSelectedReason(REWARDS)}
                >
                  <RadioGroupItem value={REWARDS} id={REWARDS} selection={selectedReason} />
                  <Label className={`text-[--base-black] font-medium cursor-pointer`} htmlFor={REWARDS}>
                    Rewards
                  </Label>
                </div>
                <div
                  className="ml-6 text-sm flex-1 mb-3 text-neutral-700 font-normal cursor-pointer"
                  onClick={() => setSelectedReason(REWARDS)}
                >
                  There is an issue with the rewards category assigned or point allocation.
                </div>
              </div>

              {/* Option Three */}
              <div className="md:ml-2 ml-3">
                <div
                  className="flex items-center space-x-2  md:w-[320px] md:h-[20px] w-[239px] h-[20px] cursor-pointer"
                  onClick={() => setSelectedReason(I_NEED_TO_DISPUTE)}
                >
                  <RadioGroupItem value={I_NEED_TO_DISPUTE} id={I_NEED_TO_DISPUTE} selection={selectedReason} />
                  <Label className={`text-[--base-black] font-medium cursor-pointer`} htmlFor={I_NEED_TO_DISPUTE}>
                    I Need To Dispute
                  </Label>
                </div>
                <div
                  className="ml-6 text-sm flex-1 text-neutral-700 font-normal cursor-pointer"
                  onClick={() => setSelectedReason(I_NEED_TO_DISPUTE)}
                >
                  Dispute a charge if you didn’t authorize it, were billed incorrectly, charged for a canceled or
                  returned item, didn’t receive what you paid for, or experienced issues with a product or service.
                </div>
              </div>
            </RadioGroup>
          </>
        )}
      </div>
      <Divider className="flex-shrink-0" />
      <div className="flex-shrink-0">{footerContent}</div>
    </DialogContent>
  )
}

ReportProblemDialog.propTypes = {
  transaction: PropTypes.shape({
    id: PropTypes.string,
    merchantName: PropTypes.string,
    merchantDetails: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    }),
    amount: PropTypes.shape({
      value: PropTypes.number,
    }),
    sign: PropTypes.string,
    transactionDate: PropTypes.string,
  }).isRequired,
}

export default ReportProblemDialog
